"use client";

import { useMemo, useState, useEffect } from "react";
import { subDays, subMonths, isEqual, endOfDay, startOfDay } from "date-fns";
import { useTranslations } from "next-intl";
import { useBase64QueryState } from "../../utils/nuqs-custom-parsers/nuqs-custom-parsers";
import { cn } from "@repo/ui/lib/utils";
import { Button } from "@repo/ui/components/ui/button";
import Icon from "@repo/ui/components/ui/icon/icon";
import { FloatingLabelDate } from "@repo/ui/components/ui/floating-label-date-picker";
import { DateRange } from "react-day-picker";

type NotificationStatus = "all" | "unread";

export type FiltersType = {
  status?: NotificationStatus;
  dateFrom?: string;
  dateTo?: string;
  activeDateRangeId?: string;
};

export default function NotificationFilters() {
  const t = useTranslations("Notifications");

  const [showFilters, setShowFilters] = useState<boolean>();

  const [filters, setFilters] = useBase64QueryState<FiltersType>(
    {
      status: "all",
    },
    {},
    "f",
  );

  const statusFilters: { id: NotificationStatus; label: string }[] =
    useMemo(() => {
      return [
        {
          id: "all",
          label: t("filters.all"),
        },
        {
          id: "unread",
          label: t("filters.unread"),
        },
      ];
    }, []);

  const handleStatusChange = (status: NotificationStatus) => {
    setFilters((prev: any) => ({
      ...prev,
      status: status,
    }));
  };

  return (
    <div className="mt-10 flex flex-col gap-5">
      <div className="flex justify-between">
        <div className="flex gap-4">
          {statusFilters.map(({ id, label }) => (
            <div key={id} className="flex flex-col items-center">
              <Button
                variant="ghost"
                onClick={() => handleStatusChange(id)}
                className={cn(
                  "p-0 text-1xl max-md:text-sm  max-md:text-[16px] text-secondary-black dark:text-primary-white",
                  filters?.status && filters.status === id
                    ? "font-black"
                    : "font-normal",
                )}
              >
                {label}
              </Button>
              {filters?.status && filters?.status === id && (
                <div className="h-2 w-2 bg-primary-black dark:bg-primary-white rounded-full"></div>
              )}
            </div>
          ))}
        </div>
        <div>
          <Button
            className={cn(
              "px-4 text-sm font-normal border-0 rounded-[5px]",
              "hover:bg-secondary-gray1/65 dark:hover:bg-secondary-gray1/30",
              showFilters
                ? "bg-primary-black text-primary-white hover:bg-primary-black " +
                    "dark:bg-primary-white dark:text-secondary-black dark:hover:bg-primary-white"
                : "text-secondary-black dark:text-primary-white bg-secondary-gray1/45 dark:bg-secondary-gray1/20",
            )}
            onClick={() => setShowFilters(!showFilters)}
          >
            {t("filters.cta_filters")}
            <Icon
              icon="Filters"
              className={cn(
                "ml-[10px] h-[16px] w-[14px]",
                showFilters
                  ? "stroke-primary-white dark:stroke-primary-magenta"
                  : "stroke-secondary-black dark:stroke-primary-white",
              )}
            />
          </Button>
        </div>
      </div>
      {showFilters && (
        <NotificationFiltersInner
          onFiltersApply={() => setShowFilters(false)}
        />
      )}
    </div>
  );
}

const NotificationFiltersInner = ({
  onFiltersApply,
}: {
  onFiltersApply?: () => void;
}) => {
  const t = useTranslations("Notifications");

  const [queryFilters, setQueryFilters] = useBase64QueryState<FiltersType>(
    {},
    {},
    "f",
  );

  const [filters, setFilters] = useState<FiltersType>(queryFilters);

  useEffect(() => {}, [filters]);

  const dateRangeFilters = useMemo(() => {
    const today = new Date();
    return [
      {
        id: "last7Days",
        label: t("filters.last_7_days"),
        dateFrom: startOfDay(subDays(today, 7)),
        dateTo: endOfDay(today),
      },
      {
        id: "last30Days",
        label: t("filters.last_30_days"),
        dateFrom: startOfDay(subDays(today, 30)),
        dateTo: endOfDay(today),
      },
      {
        id: "last60Days",
        label: t("filters.last_60_days"),
        dateFrom: startOfDay(subDays(today, 60)),
        dateTo: endOfDay(today),
      },
      {
        id: "last6Month",
        label: t("filters.last_6_months"),
        dateFrom: startOfDay(subMonths(today, 6)),
        dateTo: endOfDay(today),
      },
    ];
  }, []);

  const handleDateRangeChange = (date: DateRange) => {
    if (date?.from && date?.to) {
      const dateString = {
        dateFrom: new Date(date.from),
        dateTo: new Date(date.to),
      };

      setFilters((prev: any) => ({
        ...prev,
        ...dateString,
        activeDateRangeId:
          dateRangeFilters.find(
            (range) =>
              isEqual(range.dateFrom, dateString.dateFrom) &&
              isEqual(range.dateTo, dateString.dateTo),
          )?.id || undefined,
      }));
    }
  };

  const handleTabDateChange = (selectedId: string) => {
    const selectedDateRange = dateRangeFilters.find(
      (filter) => filter.id === selectedId,
    );
    if (!selectedDateRange) return;

    setFilters((prev: any) => ({
      ...prev,
      activeDateRangeId: selectedId,
      dateFrom: selectedDateRange.dateFrom,
      dateTo: selectedDateRange.dateTo,
    }));
  };

  function onApplyFilters() {
    setQueryFilters((prev: any) => ({
      ...filters,
      status: prev.status || "all",
    }));

    if (onFiltersApply) {
      onFiltersApply();
    }
  }

  function onCancelFilters() {
    setQueryFilters({
      status: "all",
    });
    setFilters({});
  }

  return (
    <div className="flex max-md:flex-col gap-5 p-10 max-md:p-5 w-full rounded-[20px] rounded-tr-none bg-secondary-gray2 dark:bg-secondary-black">
      <div className="flex-1">
        <h5 className="font-bold max-md:text-[16px]">
          {t("filters.filter_by_tags")}
        </h5>
        <div className="flex items-center flex-wrap gap-4 max-md:gap-3 mt-5">
          {dateRangeFilters.map(({ id, label }) => {
            return (
              <Button
                key={id}
                variant="default"
                size="slim"
                onClick={() => handleTabDateChange(id)}
                className={cn(
                  "p-4 max-md:p-3 border-0 text-base max-md:text-sm font-normal hover:text-primary-white h-6",
                  filters?.activeDateRangeId && filters.activeDateRangeId === id
                    ? "font-bold"
                    : "bg-secondary-gray1 border-0 text-primary-black dark:bg-secondary-gray1  dark:text-primary-black font-normal",
                )}
              >
                {label}
              </Button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="flex flex-col items-start">
          <h5 className="mb-7 font-bold  max-md:text-[16px]">
            {t("filters.filter_by_date")}
          </h5>
          <FloatingLabelDate
            key={`${filters.activeDateRangeId}`}
            className="w-[270px]"
            variant="slim"
            label="GG/MM/AAAA - GG/MM/AAAA"
            dateValue={{
              from: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
              to: filters.dateTo ? new Date(filters.dateTo) : undefined,
            }}
            onDateChange={(date: DateRange) => handleDateRangeChange(date)}
          />
          <div className="mt-5 flex justify-end max-md:justify-start gap-4">
            <Button
              onClick={() => onCancelFilters()}
              variant="ghost"
              className="underline text-base p-2"
              disabled={!filters.dateFrom || !filters.dateTo}
            >
              {t("filters.clear_filters")}
            </Button>
            <Button
              onClick={() => onApplyFilters()}
              variant="default"
              className="text-base"
            >
              {t("filters.apply_filters")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
